/* eslint-disable deprecation/deprecation */
import {
  StepLabel as MuiStepLabel,
  StepContext,
  StepContextType,
  StepLabelProps as MuiStepLabelProps,
  stepLabelClasses,
  styled,
  useThemeProps,
  type StepIconProps,
} from '@mui/material';
import { getToken } from '@workiva/unify-tokens';
import * as React from 'react';
import { getMuiName, setMuiName } from '../../utils/mui_name';
import { UnifySeverityStepIcon } from './UnifySeverityStepIcon';
import type { StepSeverity } from './StepSeverity';

/**
 * Props that we do not want to include with StepLabel.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type StepLabelBlockedProps = keyof Pick<MuiStepLabelProps, 'icon'>;

interface StepLabelOwnProps {
  /**
   * The severity of the step.
   * Renders a different icon based on the severity.
   *
   * @default 'default'
   */
  severity?: StepSeverity;
}

export type StepLabelProps = Omit<MuiStepLabelProps, StepLabelBlockedProps> &
  StepLabelOwnProps;

const UnifyStepLabelRoot = styled(MuiStepLabel, {
  name: 'UnifyStepLabel',
  slot: 'Root',
})<
  Omit<StepLabelProps, 'severity'> & {
    ownerState: { isInactive: boolean; usesIconGlyph: boolean };
  }
>(function UnifyStepLabelRoot({ theme, ownerState }) {
  const { isInactive, usesIconGlyph } = ownerState;

  return {
    [`.${stepLabelClasses.labelContainer}`]: {
      color: isInactive
        ? getToken('Core/Text/text-disabled')
        : getToken('Core/Text/text-secondary'),
      ...theme.typography.body2,
    },
    [`.${stepLabelClasses.label}.${stepLabelClasses.label}`]: {
      color: isInactive
        ? getToken('Core/Text/text-disabled')
        : getToken('Core/Text/text-default'),
      ...theme.typography.body1,
      fontWeight: theme.typography.fontWeightBold,

      [`&.${stepLabelClasses.alternativeLabel}`]: {
        marginTop: theme.spacing(1),
        lineHeight: '24px',
      },
    },
    [`&.${stepLabelClasses.error}`]: {
      color: getToken('Core/Text/text-default'),
    },
    ...(!usesIconGlyph && {
      [`.${stepLabelClasses.iconContainer} > svg`]: {
        // Reduce the 24px container by 1px on all sides to make the
        // svg `<circle>` el have the same size as a default unify icon (20px)
        padding: '2px',
      },
    }),
  };
});

export const StepLabel = React.forwardRef<HTMLDivElement, StepLabelProps>(
  function StepLabel(inProps, forwardedRef) {
    const props = useThemeProps({ props: inProps, name: 'MuiStepLabel' });
    const { completed, active } = React.useContext(
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      StepContext as React.Context<StepContextType>
    );

    const {
      severity: severityIn = 'default',
      // eslint-disable-next-line deprecation/deprecation
      error = severityIn === 'error',
      StepIconComponent: StepIconComponentProp,
      StepIconProps: StepIconPropsProp,
      ...rest
    } = props;

    const severity = error ? 'error' : severityIn;

    const ownerState = {
      isInactive: severity === 'default' && !error && !active && !completed,
      usesIconGlyph: severity !== 'default' || error || completed,
    };

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const iconComponent = (
      severity !== 'default' || completed
        ? UnifySeverityStepIcon
        : StepIconComponentProp
    ) as React.ElementType<StepIconProps>;

    const stepIconPropsWithSeverity = {
      ...StepIconPropsProp,
      error: error,
      ownerState: {
        severity,
      },
    };

    return (
      <UnifyStepLabelRoot
        StepIconComponent={iconComponent}
        error={error}
        StepIconProps={stepIconPropsWithSeverity}
        ownerState={ownerState}
        {...rest}
        ref={forwardedRef}
      />
    );
  }
);
setMuiName(StepLabel, getMuiName(MuiStepLabel));

export default StepLabel;
